import styled from '@emotion/styled';
import { alpha, Box, Menu, MenuItem, useTheme } from '@mui/material';
import type { Theme } from '@mui/material/styles';
import DateRangeIcon from '@mui/icons-material/DateRange';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import SearchIcon from '@mui/icons-material/Search';
import type { ChangeEvent, CSSProperties, FC, MouseEvent, ReactNode } from 'react';
import { useState } from 'react';
import { useSecurityDashboardGrid } from './useSecurityDashboardGrid';
import { SecurityClientFilter } from './SecurityClientFilter';
import {
  useWazuhScope,
  WAZUH_DATE_RANGE_OPTIONS,
  type WazuhDateRangeOption,
} from 'src/mindtime/hooks/useWazuhScope';

const DATE_RANGE_FILTER_OPTIONS = WAZUH_DATE_RANGE_OPTIONS.map((option) => ({
  label: option,
  value: option,
}));

export const isSecurityFilterApplied = (value?: string | null): boolean => Boolean(value);

export const getSecurityToolbarFilterStyles = (
  theme: Theme,
  applied: boolean,
  boxOverrides?: CSSProperties
) => {
  const accent = theme.palette.secondary.main;
  const muted = theme.palette.text.secondary;

  return {
    box: {
      display: 'flex',
      alignItems: 'center',
      gap: 8,
      height: 36,
      padding: '0 12px',
      border: `1px solid ${applied ? accent : theme.palette.divider}`,
      borderRadius: 8,
      backgroundColor: applied ? alpha(accent, 0.12) : theme.palette.background.paper,
      boxSizing: 'border-box' as const,
      width: '100%',
      minWidth: 0,
      ...boxOverrides,
    },
    text: {
      fontFamily: 'Inter, sans-serif',
      fontSize: 13,
      lineHeight: '20px',
      color: applied ? accent : muted,
      whiteSpace: 'nowrap' as const,
    },
    iconColor: applied ? accent : muted,
  };
};

export const COMPACT_TOOLBAR_FILTER_OVERRIDES: CSSProperties = {
  height: 34,
  padding: '0 10px',
  gap: 6,
  cursor: 'pointer',
  whiteSpace: 'nowrap',
  width: 'auto',
  justifyContent: 'space-between',
};

type SecurityToolbarFilterPillProps = {
  applied: boolean;
  label: ReactNode;
  onClick: (event: MouseEvent<HTMLElement>) => void;
  leadingIcon?: ReactNode;
  boxStyleOverrides?: CSSProperties;
  textStyleOverrides?: CSSProperties;
  iconSize?: number;
};

export const SecurityToolbarFilterPill: FC<SecurityToolbarFilterPillProps> = ({
  applied,
  label,
  onClick,
  leadingIcon,
  boxStyleOverrides,
  textStyleOverrides,
  iconSize = 18,
}) => {
  const theme = useTheme();
  const { box, text, iconColor } = getSecurityToolbarFilterStyles(theme, applied, {
    cursor: 'pointer',
    justifyContent: 'space-between',
    ...boxStyleOverrides,
  });

  return (
    <Box role="button" tabIndex={0} onClick={onClick} style={box}>
      <Box display="flex" alignItems="center" style={{ gap: 8, minWidth: 0, flex: 1 }}>
        {leadingIcon ? (
          <Box
            component="span"
            sx={{ display: 'flex', color: iconColor, '& svg': { fontSize: iconSize } }}
          >
            {leadingIcon}
          </Box>
        ) : null}
        <Box
          component="span"
          style={{
            ...text,
            ...textStyleOverrides,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {label}
        </Box>
      </Box>
      <KeyboardArrowDownIcon style={{ fontSize: iconSize, color: iconColor, flexShrink: 0 }} />
    </Box>
  );
};

const ToolbarSearchInput = styled.input({
  flex: 1,
  minWidth: 0,
  border: 'none',
  outline: 'none',
  background: 'transparent',
  fontFamily: 'Inter, sans-serif',
  fontSize: 13,
  lineHeight: '20px',
  padding: 0,
  '&::placeholder': {
    opacity: 0.75,
  },
});

type SecurityDashboardToolbarSearchProps = {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
};

export const SecurityToolbarSearch: FC<SecurityDashboardToolbarSearchProps> = ({
  value,
  onChange,
  placeholder = 'Search devices, agents or commands...',
}) => {
  const theme = useTheme();
  const applied = Boolean(value.trim());
  const { box, iconColor } = getSecurityToolbarFilterStyles(theme, applied);

  return (
    <Box style={box}>
      <SearchIcon style={{ fontSize: 18, color: iconColor, flexShrink: 0 }} />
      <ToolbarSearchInput
        value={value}
        onChange={(event: ChangeEvent<HTMLInputElement>) => onChange(event.target.value)}
        placeholder={placeholder}
        style={{ color: iconColor }}
      />
    </Box>
  );
};

const SecurityDashboardToolbarSearch: FC<SecurityDashboardToolbarSearchProps> = (props) => (
  <SecurityToolbarSearch {...props} />
);

export type SecurityToolbarFilterOption = {
  label: string;
  value: string;
};

export type SecurityToolbarFilterProps = {
  label: string;
  icon?: React.ReactNode;
  onClick?: () => void;
  minWidth?: number;
  compact?: boolean;
  options?: SecurityToolbarFilterOption[];
  value?: string;
  onChange?: (value: string) => void;
};

export const SecurityToolbarFilter: FC<SecurityToolbarFilterProps> = ({
  label,
  icon,
  onClick,
  minWidth,
  compact = false,
  options,
  value = '',
  onChange,
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const hasDropdown = Boolean(options?.length && onChange);
  const applied = isSecurityFilterApplied(value);
  const hasBuiltInAllOption = options?.some((option) => option.value === '');

  const displayLabel = hasDropdown
    ? options?.find((option) => option.value === value)?.label ?? label
    : label;

  const handleOpen = (event: MouseEvent<HTMLElement>) => {
    if (hasDropdown) {
      setAnchorEl(event.currentTarget);
      return;
    }
    onClick?.();
  };

  const handleSelect = (nextValue: string) => {
    onChange?.(nextValue);
    setAnchorEl(null);
  };

  return (
    <>
      <SecurityToolbarFilterPill
        applied={applied}
        label={displayLabel}
        onClick={handleOpen}
        leadingIcon={icon}
        iconSize={compact ? 16 : 18}
        textStyleOverrides={compact ? { fontSize: 12, lineHeight: '18px' } : undefined}
        boxStyleOverrides={{
          ...(compact ? COMPACT_TOOLBAR_FILTER_OVERRIDES : {}),
          width: minWidth || 'auto',
          minWidth: minWidth ?? 0,
          flex: minWidth ? `0 0 ${minWidth}px` : '0 0 auto',
        }}
      />
      {hasDropdown ? (
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={() => setAnchorEl(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          PaperProps={{ sx: { mt: 0.5, minWidth: 160 } }}
        >
          {hasBuiltInAllOption ? null : (
            <MenuItem
              selected={!value}
              onClick={() => handleSelect('')}
              style={{ fontSize: 13 }}
            >
              {`All ${label}`}
            </MenuItem>
          )}
          {options?.map((option) => (
            <MenuItem
              key={option.value}
              selected={option.value === value}
              onClick={() => handleSelect(option.value)}
              style={{ fontSize: 13 }}
            >
              {option.label}
            </MenuItem>
          ))}
        </Menu>
      ) : null}
    </>
  );
};

const SecurityDashboardToolbarDateFilter: FC = () => {
  const { dateRange, setDateRange } = useWazuhScope();

  return (
    <SecurityToolbarFilter
      label="Date Range"
      icon={<DateRangeIcon style={{ fontSize: 18 }} />}
      options={DATE_RANGE_FILTER_OPTIONS}
      value={dateRange}
      onChange={(value) => setDateRange(value as WazuhDateRangeOption)}
    />
  );
};

type SecurityDashboardToolbarProps = {
  searchValue: string;
  onSearchChange: (value: string) => void;
  searchPlaceholder?: string;
  /** When false, the reseller client filter slot is omitted entirely. */
  showClientFilter?: boolean;
  /** `inline` — fixed-width controls for header row; `full` — stretches to container width */
  layout?: 'inline' | 'full';
};

export const SecurityDashboardToolbar: FC<SecurityDashboardToolbarProps> = ({
  searchValue,
  onSearchChange,
  searchPlaceholder,
  showClientFilter = true,
  layout = 'inline',
}) => {
  const { toolbarStack } = useSecurityDashboardGrid();
  const { isReseller } = useWazuhScope();
  const isFullWidth = layout === 'full';
  const shouldShowClientFilter = showClientFilter && isReseller;

  return (
    <Box
      display="flex"
      alignItems="center"
      flexDirection={toolbarStack ? 'column' : 'row'}
      style={{
        gap: 12,
        width: toolbarStack || isFullWidth ? '100%' : 'auto',
        minWidth: 0,
        flex: toolbarStack || isFullWidth ? '1 1 auto' : '0 1 auto',
        justifyContent: isFullWidth && !toolbarStack && !shouldShowClientFilter ? 'flex-start' : undefined,
      }}
    >
      <Box
        style={{
          flex:
            isFullWidth && !toolbarStack
              ? shouldShowClientFilter
                ? '1.6 1 0'
                : '1 1 0'
              : undefined,
          width: toolbarStack ? '100%' : isFullWidth ? undefined : 300,
          minWidth: toolbarStack ? undefined : isFullWidth ? 220 : 0,
        }}
      >
        <SecurityDashboardToolbarSearch
          value={searchValue}
          onChange={onSearchChange}
          placeholder={searchPlaceholder}
        />
      </Box>
      {shouldShowClientFilter ? (
        <Box
          style={{
            flex: isFullWidth && !toolbarStack ? '1 1 0' : undefined,
            width: toolbarStack ? '100%' : isFullWidth ? undefined : 160,
            minWidth: toolbarStack ? undefined : isFullWidth ? 150 : 0,
          }}
        >
          <SecurityClientFilter />
        </Box>
      ) : null}
      <Box
        style={{
          flex: isFullWidth && !toolbarStack && shouldShowClientFilter ? '1 1 0' : undefined,
          width: toolbarStack ? '100%' : isFullWidth ? undefined : 140,
          minWidth: toolbarStack ? undefined : isFullWidth ? 140 : 0,
          flexShrink: isFullWidth && !toolbarStack && !shouldShowClientFilter ? 0 : undefined,
        }}
      >
        <SecurityDashboardToolbarDateFilter />
      </Box>
    </Box>
  );
};

type SecurityDashboardHeaderProps = {
  searchValue: string;
  onSearchChange: (value: string) => void;
  showClientFilter?: boolean;
  welcomeLabel?: string;
};

export const SecurityDashboardHeader: FC<SecurityDashboardHeaderProps> = ({
  searchValue,
  onSearchChange,
  showClientFilter = true,
  welcomeLabel,
}) => {
  const { toolbarStack } = useSecurityDashboardGrid();
  const { userDisplayName, isReseller } = useWazuhScope();
  const resolvedWelcome = welcomeLabel ?? `Welcome ${userDisplayName}`;
  const shouldShowClientFilter = showClientFilter && isReseller;

  return (
    <Box
      display="flex"
      alignItems="center"
      justifyContent={toolbarStack ? 'flex-start' : 'space-between'}
      flexDirection={toolbarStack ? 'column' : 'row'}
      style={{
        gap: 16,
        width: '100%',
        flexWrap: 'wrap',
      }}
    >
      <Box
        component="span"
        style={{
          fontFamily: 'Inter, sans-serif',
          fontSize: 22,
          fontWeight: 700,
          lineHeight: '28px',
          color: '#1E293B',
          flexShrink: 0,
          alignSelf: toolbarStack ? 'flex-start' : undefined,
        }}
      >
        {resolvedWelcome}
      </Box>
      <SecurityDashboardToolbar
        searchValue={searchValue}
        onSearchChange={onSearchChange}
        showClientFilter={shouldShowClientFilter}
        layout="inline"
      />
    </Box>
  );
};
